import React, { useState, useEffect } from "react";
import posthog from "posthog-js";
import { buildDefaultRedirectURL, readUtmSource } from "../../../utils/signupHandoff";
import SignupPopup from "../../SignupPopup/SignupPopup";
import HeroPromptBar from "./HeroPromptBar/HeroPromptBar";
import { PORTAL_BASE_URL } from "../../../env";
import { trackCta } from "../../../analytics";
import { redirectToRegister } from "../../../utils/registerRedirect";
import ASSETS from "../assets.json";
import "./HeroSection.scss";

const AB_FLAG = "ab-landing-page";
const VARIANT_KEY = "landing-headline";

const COPY = {
    control: {
        headline: <>AI assets for your store,<br />fast and accurate</>,
        sub: "Generate product images, videos, and marketing assets with control in minutes.",
    },
    test: {
        headline: "Get to Market in Days, Not Weeks",
        sub: "Rapidly generate product images, videos, and marketing assets in minutes.",
    },
};

function resolveVariant() {
    try {
        if (window._posthogLoaded && posthog.getFeatureFlag(AB_FLAG) === VARIANT_KEY) {
            return "test";
        }
    } catch {}
    return "control";
}

const MOBILE_MEDIA_QUERY = "(max-width: 991px)";

const COLUMNS = [
    {
        cls: "lv2-gallery-col-1",
        images: [
            { src: ASSETS.hero.galleryCol1[0], height: 180, radius: 8 },
            { src: ASSETS.hero.galleryCol1[1], height: 450, radius: 24 },
            { src: ASSETS.hero.galleryCol1[2], height: 322, radius: 24 },
            { src: ASSETS.hero.galleryCol1[3], height: 180, radius: 24 },
            { src: ASSETS.hero.galleryCol1[4], height: 322, radius: 24 },
        ],
    },
    {
        cls: "lv2-gallery-col-2",
        images: [
            { src: ASSETS.hero.galleryCol2[0], height: 322, radius: 8 },
            { src: ASSETS.hero.galleryCol2[1], height: 483, radius: 24 },
            { src: ASSETS.hero.galleryCol2[2], height: 180, radius: 24 },
            { src: ASSETS.hero.galleryCol2[3], height: 322, radius: 8 },
        ],
    },
    {
        cls: "lv2-gallery-col-3",
        images: [
            { src: ASSETS.hero.galleryCol3[0], height: 180, radius: 8 },
            { src: ASSETS.hero.galleryCol3[1], height: 573, radius: 24 },
            { src: ASSETS.hero.galleryCol3[2], height: 322, radius: 24 },
            { src: ASSETS.hero.galleryCol3[3], height: 322, radius: 8 },
        ],
    },
    {
        cls: "lv2-gallery-col-4",
        images: [
            { src: ASSETS.hero.galleryCol4[0], height: 322, radius: 8 },
            { src: ASSETS.hero.galleryCol4[1], height: 180, radius: 24 },
            { src: ASSETS.hero.galleryCol4[2], height: 322, radius: 24 },
            { src: ASSETS.hero.galleryCol4[3], height: 577, radius: 24 },
        ],
    },
];

const handleStartFree = () => {
    trackCta("free_trial_click", {
        section: "hero",
        button:  "Start Free",
    });
    redirectToRegister("/select-subscription?plan=free_trial_ai&plan_type=smb_3d_services");
};

const HeroSection = () => {
    const [signupOpen, setSignupOpen] = useState(false);
    const [redirectURL, setRedirectURL] = useState(() =>
        buildDefaultRedirectURL(readUtmSource())
    );
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== "undefined" && window.matchMedia(MOBILE_MEDIA_QUERY).matches
    );
    // Synchronous initializer catches cached flags for returning visitors with
    // prior consent — no flicker. onFeatureFlags() below handles first-time loads.
    const [variant, setVariant] = useState(resolveVariant);

    useEffect(() => {
        const unsubscribe = posthog.onFeatureFlags(() => {
            setVariant(
                posthog.getFeatureFlag(AB_FLAG) === VARIANT_KEY ? "test" : "control"
            );
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
        const onChange = (e) => setIsMobile(e.matches);
        mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
        return () => {
            mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
        };
    }, []);

    // HeroPromptBar computes the redirect URL on Generate click and passes it up.
    const handleRequestSignup = (url) => {
        setRedirectURL(url);
        setSignupOpen(true);
    };

    const visibleColumns = isMobile ? COLUMNS.slice(0, 2) : COLUMNS;

    return (
        <div className="lv2-hero-wrapper">
            {/* Radial blue gradient behind the heading */}
            <div className="lv2-hero-bg" />

            {/* Heading */}
            <div className="lv2-hero-heading">
                <h1 className="manrope w-600 white lv2-hero-title">
                    {COPY[variant].headline}
                </h1>
                <p className="manrope f-20 w-500 lh-28 white lv2-hero-subtitle">
                    {COPY[variant].sub}
                </p>
            </div>

            {/* Scrolling image gallery */}
            <div className="lv2-gallery">
                <div className="lv2-gallery-columns">
                    {visibleColumns.map((col, ci) => (
                        <div key={ci} className={`lv2-gallery-col ${col.cls}`}>
                            {col.images.map((img, ii) => (
                                <div
                                    key={ii}
                                    className="lv2-gallery-img"
                                    style={{ height: img.height, borderRadius: img.radius }}
                                >
                                    <picture>
                                        <source media={MOBILE_MEDIA_QUERY} srcSet={img.src.mobile} />
                                        <img src={img.src.desktop} decoding="async" alt="" />
                                    </picture>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Prompt bar (extracted) */}
            <HeroPromptBar onRequestSignup={handleRequestSignup} />

            <SignupPopup
                visible={signupOpen}
                onCancel={() => setSignupOpen(false)}
                redirectURL={redirectURL}
            />
        </div>
    );
};

export default HeroSection;
